import React, { useState, useEffect, useCallback } from 'react';
import './PwaUpdateBanner.css';

const DISMISS_SESSION_KEY = 'gcg_pwa_update_dismiss_session';

/** Same id baked at build time (see scripts/inject-build-env.js) and in /build-version.json */
const EMBEDDED_BUILD_ID =
  typeof process !== 'undefined' && process.env.REACT_APP_BUILD_ID
    ? String(process.env.REACT_APP_BUILD_ID)
    : '';

/**
 * Compares running bundle build id to /build-version.json from the server.
 * If they differ, the user is on a cached PWA shell — prompt refresh.
 */
export default function PwaUpdateBanner() {
  const [visible, setVisible] = useState(false);

  const check = useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (process.env.NODE_ENV !== 'production') return;
    if (!EMBEDDED_BUILD_ID) return;
    if (sessionStorage.getItem(DISMISS_SESSION_KEY) === '1') return;

    try {
      const res = await fetch(`${window.location.origin}/build-version.json`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const data = await res.json();
      const remote = data && data.v != null ? String(data.v) : null;
      if (!remote) return;

      if (remote !== EMBEDDED_BUILD_ID) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    } catch {
      /* offline */
    }
  }, []);

  useEffect(() => {
    void check();
    const id = window.setInterval(() => void check(), 30 * 60 * 1000);
    const onVis = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, [check]);

  const applyUpdate = () => {
    window.location.reload();
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_SESSION_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="pwa-update-banner" role="status">
      <div className="pwa-update-banner-inner">
        <span className="pwa-update-banner-text">A new version of the site is ready.</span>
        <div className="pwa-update-banner-actions">
          <button type="button" className="pwa-update-banner-btn pwa-update-banner-btn--primary" onClick={applyUpdate}>
            Refresh
          </button>
          <button type="button" className="pwa-update-banner-btn pwa-update-banner-btn--ghost" onClick={dismiss}>
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
